import { ScannerData } from '../types/ScannerData';
import { formatDate } from './dateUtils';
import { getAvailableMoneynessC, MoneynessC } from './moneynessCUtils';
import { getAvailableMoneynessCategories } from './moneynessUtils';
import { getMappedSymbol } from './optionsUtils';

/** Prefer first non-empty value; avoids `??` keeping `''` so `underlyingSymbol` is never read. */
function firstNonEmptyUnderlyingField(...values: unknown[]): string {
  for (const v of values) {
    if (v === null || v === undefined) continue;
    const s = String(v).trim();
    if (s.length) return s;
  }
  return '';
}

// Extract underlying consistently from row fields (not the option contract Symbol ticker).
export const getUnderlyingSymbol = (item: any): string => {
  const u = firstNonEmptyUnderlyingField(
    item?.Underlying,
    item?.underlyingSymbol,
    item?.underlying_symbol
  );
  return u || 'Unknown';
};

/** Distinct display underlyings for the current filtered scanner rows (e.g. above chart when filter is All). */
export function collectDistinctMappedUnderlyings(
  rows: readonly unknown[] | null | undefined,
  isSymbolMappingEnabled: boolean
): string[] {
  if (!rows?.length) return [];
  const set = new Set<string>();
  for (const row of rows) {
    const u = getMappedSymbol(getUnderlyingSymbol(row), isSymbolMappingEnabled);
    if (u && u !== 'Unknown') set.add(u);
  }
  return Array.from(set).sort();
}

/** Legacy RTL / index options: row may be $SPXW.X while the dropdown still has SPXW or $SPXW (stale API or fallback list). */
export function legacyUnderlyingSelectionMatches(itemUnderlying: unknown, selected: string[]): boolean {
  if (!selected.length) return true;
  const u = String(itemUnderlying ?? '').trim();
  if (!u) return false;
  if (selected.includes(u)) return true;
  const root = (s: string) =>
    s
      .trim()
      .replace(/^\$/u, '')
      .replace(/\.X$/iu, '')
      .toUpperCase();
  const uRoot = root(u);
  for (const s of selected) {
    const t = String(s ?? '').trim();
    if (!t) continue;
    if (t === u) return true;
    if (root(t) === uRoot && uRoot.length >= 1) return true;
  }
  return false;
}

// Extract expiry date from record (CBOE/backend may use different keys)
export const getExpiryFromRecord = (item: any): string => {
  const raw = item['Expiry Date'] ?? item['Expiration Date'] ?? item.expiry_date ?? item.expiryDate ?? item.Expiry ?? '';
  if (!raw) return '';
  const formatted = formatDate(raw);
  if (formatted) return formatted;
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return s;
};

// Helper function to extract available filter options from data
export const getFilterOptions = (data: ScannerData[]) => {
  // Add proper data validation
  if (!data || !Array.isArray(data) || data.length === 0) {
    return {
      availableUnderlyings: [],
      availableExpiries: [],
      availableSides: [],
      availableMoneyness: [],
      availableMoneynessC: []
    };
  }

  // Filter out invalid/non-option records first (use Underlying field only for validation).
  const validOptionData = data.filter(item => {
    const rawU = (item as any).Underlying ?? (item as any).underlying ?? '';
    const underlying = typeof rawU === 'string' && rawU.trim() ? rawU.trim() : 'Unknown';
    const optionType = String((item as any)['Option Type'] || '').toUpperCase();
    const strike = item.Strike;
    const expiry = item['Expiry Date'];
    
    // Filter out records that are clearly not options
    if (!underlying || underlying === 'Unknown') return false;
    if (!optionType || (optionType !== 'CALL' && optionType !== 'PUT' && optionType !== 'C' && optionType !== 'P')) return false;
    if (!strike || strike === 0) return false;
    if (!expiry) return false;
    
    // Filter out system processes and invalid entries
    if (underlying.includes('.exe') || underlying.includes('python') || underlying.includes('pwsh')) return false;
    if (underlying === 'Underlying' || underlying === 'Name' || underlying === 'Symbol') return false;
    
    return true;
  });

  // Use only the Underlying field from scanner data for the filter list (never Symbol or option contract ticker).
  const availableUnderlyings = Array.from(new Set(validOptionData.map(item => {
    const u = (item as any).Underlying ?? (item as any).underlying ?? '';
    const s = typeof u === 'string' ? u.trim() : '';
    return s || null;
  }))).filter((x): x is string => typeof x === 'string' && x.length > 0).sort();

  const availableExpiries = Array.from(new Set(validOptionData.map(item => 
    getExpiryFromRecord(item) || 'Unknown'
  ))).filter(Boolean).filter(x => x !== 'Unknown').sort();

  const availableSides = Array.from(new Set(validOptionData.map(item => {
    const raw = String((item as any)['Option Type'] || 'Unknown').toUpperCase();
    if (raw === 'CALL' || raw === 'C') return 'C';
    if (raw === 'PUT' || raw === 'P') return 'P';
    return raw;
  }))).filter(Boolean);

  // Calculate moneyness categories from filtered data
  const availableMoneyness = getAvailableMoneynessCategories(validOptionData);

  // Get MoneynessC options (ITM/ATM/OTM)
  const availableMoneynessC = getAvailableMoneynessC(validOptionData);

  return {
    availableUnderlyings,
    availableExpiries,
    availableSides,
    availableMoneyness,
    availableMoneynessC
  };
};

// Default filter props for backward compatibility
export const getDefaultFilterProps = (data: ScannerData[]) => {
  const { availableUnderlyings, availableExpiries, availableSides, availableMoneyness } = getFilterOptions(data);
  
  return {
    selectedSide: 'all',
    selectedMoneyness: 'all',
    availableSides,
    availableMoneyness,
    onSideChange: () => {},
    onMoneynessChange: () => {}
  };
}; 

/**
 * Ensures proper numerical sorting of strike prices
 * Handles string/number conversion and edge cases
 */
export const sortByStrikePrice = <T extends { strike: string | number }>(data: T[]): T[] => {
  return data.sort((a, b) => {
    const strikeA = typeof a.strike === 'string' ? parseFloat(a.strike) || 0 : Number(a.strike) || 0;
    const strikeB = typeof b.strike === 'string' ? parseFloat(b.strike) || 0 : Number(b.strike) || 0;
    return strikeA - strikeB;
  });
};

/**
 * Ensures proper numerical sorting of any field that should be treated as a number
 */
export const sortByNumericField = <T>(data: T[], field: keyof T): T[] => {
  return data.sort((a, b) => {
    const valueA = typeof a[field] === 'string' ? parseFloat(String(a[field])) || 0 : Number(a[field]) || 0;
    const valueB = typeof b[field] === 'string' ? parseFloat(String(b[field])) || 0 : Number(b[field]) || 0;
    return valueA - valueB;
  });
};

/**
 * Simple and robust strike sorting function that actually works
 */
export const simpleStrikeSort = <T>(data: T[]): T[] => {
  if (!data || data.length === 0) return data;
  
  return data.sort((a, b) => {
    const aAny = a as any;
    const bAny = b as any;
    
    // Get strike values with fallbacks
    let aStrike = 0;
    let bStrike = 0;
    
    // Try different field names for item A
    if (aAny.priceZone !== undefined && aAny.priceZone !== null) {
      aStrike = parseFloat(String(aAny.priceZone)) || 0;
    } else if (aAny.strike !== undefined && aAny.strike !== null) {
      aStrike = parseFloat(String(aAny.strike)) || 0;
    } else if (aAny.Strike !== undefined && aAny.Strike !== null) {
      aStrike = parseFloat(String(aAny.Strike)) || 0;
    } else if (aAny.name !== undefined && aAny.name !== null) {
      const match = String(aAny.name).match(/(\d+(?:\.\d+)?)/);
      if (match) aStrike = parseFloat(match[1]) || 0;
    }
    
    // Try different field names for item B
    if (bAny.priceZone !== undefined && bAny.priceZone !== null) {
      bStrike = parseFloat(String(bAny.priceZone)) || 0;
    } else if (bAny.strike !== undefined && bAny.strike !== null) {
      bStrike = parseFloat(String(bAny.strike)) || 0;
    } else if (bAny.Strike !== undefined && bAny.Strike !== null) {
      bStrike = parseFloat(String(bAny.Strike)) || 0;
    } else if (bAny.name !== undefined && bAny.name !== null) {
      const match = String(bAny.name).match(/(\d+(?:\.\d+)?)/);
      if (match) bStrike = parseFloat(match[1]) || 0;
    }
    
    return aStrike - bStrike;
  });
};

/**
 * Universal strike sorting function for all chart components
 */
export const universalStrikeSort = <T>(data: T[]): T[] => {
  return simpleStrikeSort(data);
};

/**
 * Debug function to check strike sorting
 */
export const debugStrikeSorting = <T>(data: T[], name: string = 'Chart'): void => {
  if (data.length > 0) {
    console.log(`${name} Strike Sorting Debug:`, {
      totalItems: data.length,
      first5Items: data.slice(0, 5).map((item, index) => {
        const strike = (item as any).strike || (item as any).Strike || (item as any).priceZone || 'N/A';
        return { index, strike, parsed: parseFloat(String(strike)) || 0 };
      }),
      last5Items: data.slice(-5).map((item, index) => {
        const strike = (item as any).strike || (item as any).Strike || (item as any).priceZone || 'N/A';
        return { index: data.length - 5 + index, strike, parsed: parseFloat(String(strike)) || 0 };
      })
    });
  }
}; 

/**
 * Test function to check data structure and sorting
 */
export const testStrikeSorting = <T>(data: T[], name: string = 'Test'): void => {
  console.log(`🧪 ${name} - Testing Strike Sorting:`, {
    dataLength: data.length,
    sampleItems: data.slice(0, 5).map((item, index) => {
      const itemAny = item as any;
      return {
        index,
        priceZone: itemAny.priceZone,
        strike: itemAny.strike,
        Strike: itemAny.Strike,
        name: itemAny.name,
        allKeys: Object.keys(itemAny).filter(key => key.toLowerCase().includes('strike') || key.toLowerCase().includes('price'))
      };
    })
  });
}; 